import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./LandingPage.css";

const LandingPage = () => {
  const navigate = useNavigate();
  const [navStuck, setNavStuck] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "", eventType: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const onScroll = () => setNavStuck(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ro = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("on"); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".lp .reveal").forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  }, []);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await fetch(process.env.REACT_APP_API_URL + "/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", phone: "", company: "", eventType: "", message: "" });
        setTimeout(() => { setContactModalOpen(false); setSubmitStatus(null); }, 2000);
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqItems = [
    { q: "What's included in the ₹40,000/event plan?", a: "Everything — custom ID card design, unlimited registrations, QR check-in, Razorpay integration, WhatsApp delivery, walk-in support, live dashboard, bulk upload, and 24/7 support. One price, no add-ons, no surprises." },
    { q: "Is there a limit on attendees?", a: "No limit at all. The ₹40,000 plan covers unlimited participants. We've handled events from 200 to 5,000+ attendees without any change in pricing." },
    { q: "How customizable are the ID cards?", a: "Very. You can add your logo, choose colors, fonts, and layout. Include any fields — name, designation, company, badge category, photo, QR. Portrait and landscape formats, multiple badge types (Speaker, Delegate, VIP, Staff, Press)." },
    { q: "Does Razorpay charge extra fees?", a: "Razorpay charges their standard ~2% transaction fee per payment — this goes directly to them. We don't take a cut of your ticket revenue. We'll help you set up your Razorpay account during onboarding at no extra charge." },
    { q: "Do attendees need to download anything?", a: "No app for attendees. They get their ID card via WhatsApp or email and show the QR code at the gate. Your check-in staff scan with any standard smartphone camera — no dedicated hardware needed." },
    { q: "How long does setup take?", a: "Most organizers are fully live within 1 hour. After booking a demo, we schedule a 30–45 minute onboarding call to customize your setup, design your ID card, and test the full flow together. We recommend starting at least 2–3 weeks before your event." },
  ];

  return (
    <div className="lp">
      {/* ─── NAV ─── */}
      <nav className={navStuck ? "stuck" : ""}>
        <div className="nav-in">
          <a href="#" className="nav-logo">
            <img src="/logo/logo.png" alt="InsideOut" className="nav-logo-img" />
          </a>
          <div className="nav-links">
            <a href="#features">Product</a>
            <a href="#process">How it works</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </div>
          <button className="btn-nav" onClick={() => navigate("/login")}>Login →</button>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section id="hero">
        <div className="hero-grain"></div>
        <div className="hero-top">
          <div className="hero-kicker">Event ID Management</div>
          <h1 className="hero-h1">
            <span className="hw hw1">Your events,</span><br />
            <span className="hw hw2"><em>professionally</em></span>{" "}
            <span className="hw hw3">run.</span>
          </h1>
          <p className="hero-sub hero-sub-in">Custom ID cards, real-time check-ins, and Razorpay payments — built specifically for event organizers in India.</p>
          <div className="hero-actions hero-act-in">
            <button className="btn-hero-p" onClick={() => setContactModalOpen(true)}>Book a free demo →</button>
            <a href="#features" className="btn-hero-s">See the product</a>
          </div>
        </div>

        <div className="hero-product hero-prod-in">
          <div className="browser-frame">
            <div className="browser-bar">
              <div className="b-dot" style={{background:'#FF5F56'}}></div>
              <div className="b-dot" style={{background:'#FFBD2E'}}></div>
              <div className="b-dot" style={{background:'#27C93F'}}></div>
              <div className="b-url"><span>app.insideoutevents.in/dashboard</span></div>
              <div className="db-live" style={{marginLeft:'auto'}}>
                <div className="db-live-dot"></div>
                <span style={{fontSize:'11px'}}>Live</span>
              </div>
            </div>
            <div className="dashboard">
              <div className="db-sidebar">
                <div className="db-logo">
                  <div className="db-logo-icon"><svg viewBox="0 0 16 16"><path d="M3 2h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Zm5 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"/></svg></div>
                  <span className="db-logo-name">InsideOut</span>
                </div>
                <div className="db-section-label">Main</div>
                <div className="db-nav-item active"><svg viewBox="0 0 16 16"><path d="M2 3h5v5H2zm7 0h5v5H9zm-7 7h5v5H2zm7 0h5v5H9z"/></svg>Dashboard</div>
                <div className="db-nav-item"><svg viewBox="0 0 16 16"><path d="M8 1.5A6.5 6.5 0 1 0 14.5 8 6.507 6.507 0 0 0 8 1.5ZM8 12a.75.75 0 1 1 .75-.75A.75.75 0 0 1 8 12Zm.75-3.5a.75.75 0 0 1-1.5 0v-4a.75.75 0 0 1 1.5 0Z"/></svg>ID Cards</div>
                <div className="db-nav-item"><svg viewBox="0 0 16 16"><path d="M8 1a7 7 0 1 0 7 7A7.008 7.008 0 0 0 8 1ZM7 4h2v5H7Zm0 6h2v2H7Z"/></svg>Registrations</div>
                <div className="db-nav-item"><svg viewBox="0 0 16 16"><path d="M2 4h12v2H2zm0 5h12v2H2zm0 5h12v2H2z"/></svg>Check-in</div>
                <div className="db-section-label">Finance</div>
                <div className="db-nav-item"><svg viewBox="0 0 16 16"><path d="M13 3H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1ZM3 8h2v2H3Zm4 0h6v2H7ZM3 5h10v2H3Z"/></svg>Payments</div>
                <div className="db-section-label">Event</div>
                <div className="db-nav-item"><svg viewBox="0 0 16 16"><path d="M13 2H3a1 1 0 0 0-1 1v11l2-2h9a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1Z"/></svg>WhatsApp</div>
              </div>
              <div className="db-main">
                <div className="db-header">
                  <div>
                    <div className="db-title">TechSummit Bangalore 2026</div>
                    <div style={{fontSize:'11px',color:'rgba(255,255,255,.3)',marginTop:'2px'}}>Tomorrow · KTPO Convention Centre</div>
                  </div>
                  <div className="db-actions">
                    <div className="db-btn">Export CSV</div>
                    <div className="db-btn">WhatsApp all</div>
                    <div className="db-btn primary">Check-in mode</div>
                  </div>
                </div>
                <div className="db-stats">
                  <div className="db-stat"><div className="db-stat-label">Registered</div><div className="db-stat-val">1,847</div><div className="db-stat-delta">↑ 23 today</div></div>
                  <div className="db-stat"><div className="db-stat-label">ID Cards Ready</div><div className="db-stat-val">1,847</div><div className="db-stat-delta" style={{color:'rgba(255,255,255,.3)'}}>All generated</div></div>
                  <div className="db-stat"><div className="db-stat-label">Revenue</div><div className="db-stat-val">₹4.2L</div><div className="db-stat-delta">↑ ₹18k today</div></div>
                  <div className="db-stat"><div className="db-stat-label">WhatsApp Sent</div><div className="db-stat-val">1,831</div><div className="db-stat-delta">99.1% delivered</div></div>
                </div>
                <div className="db-cards-label">Recently generated ID cards</div>
                <div className="db-id-grid">
                  {[
                    {bg:'linear-gradient(135deg,#5B4CE4,#7B6EF5)',avBg:'rgba(91,76,228,.15)',name:'Priya S.',role:'Speaker',qr:[1,1,0,1,0,1,1,0,1,1,0,1,1,0,1,1]},
                    {bg:'linear-gradient(135deg,#1A7A4A,#22C55E)',avBg:'rgba(26,122,74,.15)',name:'Rahul M.',role:'Delegate',qr:[1,0,1,1,1,0,1,1,0,1,1,0,1,1,0,1]},
                    {bg:'linear-gradient(135deg,#B45309,#F59E0B)',avBg:'rgba(180,83,9,.15)',name:'Anita K.',role:'Organizer',qr:[0,1,1,0,1,1,0,1,0,1,1,1,1,0,1,0]},
                    {bg:'linear-gradient(135deg,#0E7490,#06B6D4)',avBg:'rgba(14,116,144,.15)',name:'Dev R.',role:'Press',qr:[1,0,1,0,1,1,0,1,1,1,0,1,0,1,1,1]},
                  ].map((c, i) => (
                    <div className="id-mini" key={i}>
                      <div className="id-mini-head" style={{background:c.bg}}></div>
                      <div className="id-mini-body">
                        <div className="id-mini-av" style={{background:c.avBg}}><svg viewBox="0 0 12 12"><circle cx="6" cy="4" r="2.5"/><path d="M1 11a5 5 0 0 1 10 0z"/></svg></div>
                        <div className="id-mini-name">{c.name}</div>
                        <div className="id-mini-role">{c.role}</div>
                        <div className="id-mini-qr">{c.qr.map((v,j) => <div className={`qp${v===0?' x':''}`} key={j}></div>)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="db-live" style={{marginTop:'14px',marginBottom:'8px'}}>
                  <div className="db-live-dot"></div>
                  <span style={{fontSize:'11px',color:'rgba(255,255,255,.3)'}}>Live check-in feed</span>
                </div>
                <div className="db-feed">
                  <div className="db-feed-item">
                    <div className="db-feed-av" style={{background:'#5B4CE4'}}>PS</div>
                    <div className="db-feed-name">Priya Sharma · Speaker</div>
                    <div className="db-feed-time">2s ago</div>
                    <div className="db-feed-check"><svg viewBox="0 0 10 10"><path d="M2 5l2.5 2.5L8 3"/></svg></div>
                  </div>
                  <div className="db-feed-item">
                    <div className="db-feed-av" style={{background:'#1A7A4A'}}>RM</div>
                    <div className="db-feed-name">Rahul Mehta · Delegate</div>
                    <div className="db-feed-time">18s ago</div>
                    <div className="db-feed-check"><svg viewBox="0 0 10 10"><path d="M2 5l2.5 2.5L8 3"/></svg></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── LOGOS ─── */}
      <section id="logos">
        <div className="logos-inner">
          <span className="logos-label">Trusted at</span>
          <div className="logos-sep"></div>
          <div className="logos-scroll">
            {["TechSummit India","NextFest Bangalore","CorpConnect Mumbai","StartupExpo Delhi","MusicMela Ahmedabad","DesignWeek Pune"].map(l => (
              <div className="logo-pill" key={l}>{l}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section id="features" className="sec">
        <div className="wrap">
          <div className="feat-intro reveal">
            <div className="feat-kicker">The product</div>
            <h2>Everything your event needs. Nothing it doesn't.</h2>
            <p>Built from the ground up for Indian event organizers — not adapted from some global template.</p>
          </div>

          {/* Row 1: ID Card Design */}
          <div className="feat-row">
            <div className="feat-copy reveal">
              <div className="feat-num">01</div>
              <h3>Design beautiful, branded ID cards in minutes</h3>
              <p>Full control over layout, colors, badge categories, and fields. Portrait or landscape. Every card ships with a unique QR code for gate scanning.</p>
              <div className="feat-tags">
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>Custom templates</span>
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>Print-ready PDF</span>
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>QR on every card</span>
              </div>
              <div className="testi-inline">
                <div className="testi-stars">
                  {[1,2,3,4,5].map(s => <svg key={s} viewBox="0 0 12 12"><path d="M6 1l1.5 3 3.5.5-2.5 2.4.6 3.5L6 9l-3.1 1.4.6-3.5L1 4.5 4.5 4z"/></svg>)}
                </div>
                <blockquote>"The branded ID cards gave our festival a <em>genuinely premium feel</em> — attendees actually commented on them."</blockquote>
                <div className="testi-person">
                  <div className="testi-av" style={{background:'#B45309'}}>AP</div>
                  <div>
                    <div className="testi-name">Ankit Patel</div>
                    <div className="testi-role">Festival Director</div>
                    <div className="testi-event">MusicMela 2024 · 3,200 attendees</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="feat-visual reveal d1">
              <div className="ui-panel">
                <div className="id-editor">
                  <div className="ide-toolbar">
                    <div className="ide-tool active"><svg viewBox="0 0 13 13"><rect x="1" y="1" width="11" height="11" rx="1"/></svg></div>
                    <div className="ide-tool"><svg viewBox="0 0 13 13"><circle cx="6.5" cy="6.5" r="5"/></svg></div>
                    <div className="ide-tool"><svg viewBox="0 0 13 13"><path d="M2 2h9M2 6.5h9M2 11h6"/></svg></div>
                    <div style={{flex:1}}></div>
                    <div style={{fontSize:'11px',color:'var(--ink3)'}}>Preview mode</div>
                  </div>
                  <div className="ide-canvas">
                    <div className="ide-photo"><svg viewBox="0 0 28 28"><circle cx="14" cy="10" r="5"/><path d="M3 26a11 11 0 0 1 22 0"/></svg></div>
                    <div className="ide-info">
                      <div className="ide-name-field">Priya Sharma</div>
                      <div className="ide-role-field">Speaker · Track A</div>
                      <div className="ide-event">TechSummit Bangalore 2026</div>
                    </div>
                    <div className="ide-qr-block">
                      {[1,1,0,1,0,1,1,0,1,0,1,1,1,1,0,1].map((v,i) => <div className={`qp${v===0?' x':''}`} key={i}></div>)}
                    </div>
                  </div>
                  <div className="ide-controls">
                    <div className="ide-ctrl">
                      <div className="ide-ctrl-label">Badge Color</div>
                      <div className="ide-color-row">
                        {['#5B4CE4','#1A7A4A','#B45309','#0E7490','#0E0D0C'].map((c,i) => (
                          <div className={`ide-swatch${i===0?' active':''}`} style={{background:c}} key={c}></div>
                        ))}
                      </div>
                    </div>
                    <div className="ide-ctrl"><div className="ide-ctrl-label">Format</div><div className="ide-ctrl-val">Portrait · CR80</div></div>
                    <div className="ide-ctrl"><div className="ide-ctrl-label">Badge Type</div><div className="ide-ctrl-val">Speaker</div></div>
                    <div className="ide-ctrl"><div className="ide-ctrl-label">QR Code</div><div className="ide-ctrl-val">Enabled ✓</div></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: QR Check-in */}
          <div className="feat-row flip">
            <div className="feat-copy reveal">
              <div className="feat-num">02</div>
              <h3>Check-in 1,000 people without a single queue</h3>
              <p>Every attendee gets a QR code on their ID card. Your team scans with any smartphone — no dedicated hardware, no app install, works offline too.</p>
              <div className="feat-tags">
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>Any smartphone</span>
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>Under 2 seconds</span>
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>Offline mode</span>
              </div>
            </div>
            <div className="feat-visual reveal d1">
              <div className="ui-panel">
                <div className="checkin-panel">
                  <div className="cp-header">
                    <span className="cp-title">Gate Scanner — Entry A</span>
                    <div className="cp-live"><div className="cp-live-dot"></div>Live</div>
                  </div>
                  <div className="cp-scanner">
                    <div className="cp-scan-frame">
                      <div className="cp-scan-inner">
                        {[1,1,0,1,1,0,0,1,1,0,1,1,1,0,1,1,0,1,1,1,0,1,1,0,0,1,1,0,1,1,1,0,1,1,0,1].map((v,i) => <div className={v===0?'x':''} key={i}></div>)}
                      </div>
                    </div>
                    <div className="cp-scan-line"></div>
                  </div>
                  <div className="cp-recent">
                    <div className="cp-recent-label">Recent check-ins</div>
                    {[
                      {av:'#5B4CE4',init:'PS',name:'Priya Sharma',badge:'Speaker · #SP-001',time:'2s ago'},
                      {av:'#1A7A4A',init:'RM',name:'Rahul Mehta',badge:'Delegate · #DL-247',time:'18s ago'},
                      {av:'#B45309',init:'AK',name:'Anita Kumar',badge:'VIP · #VP-014',time:'45s ago'},
                    ].map((item) => (
                      <div className="cp-item" key={item.init}>
                        <div className="cp-item-av" style={{background:item.av}}>{item.init}</div>
                        <div className="cp-item-info">
                          <div className="cp-item-name">{item.name}</div>
                          <div className="cp-item-badge">{item.badge}</div>
                        </div>
                        <div className="cp-item-time">{item.time}</div>
                        <div className="cp-check"><svg viewBox="0 0 10 10"><path d="M2 5l2 2 4-4" stroke="#1A7A4A" strokeWidth="1.5" fill="none"/></svg></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: WhatsApp + Payments */}
          <div className="feat-row">
            <div className="feat-copy reveal">
              <div className="feat-num">03</div>
              <h3>Payments and WhatsApp delivery, fully automated</h3>
              <p>Attendees register, pay via Razorpay (UPI, cards, net banking), and receive their ID card on WhatsApp — all without you lifting a finger.</p>
              <div className="feat-tags">
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>Razorpay integrated</span>
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>WhatsApp delivery</span>
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>Instant confirmation</span>
              </div>
            </div>
            <div className="feat-visual reveal d1">
              <div className="ui-panel">
                <div className="wa-panel">
                  <div className="wa-header">
                    <div className="wa-av"><svg viewBox="0 0 18 18"><path d="M9 1a8 8 0 1 0 8 8A8.01 8.01 0 0 0 9 1ZM6.5 13.5 2 16l2.5-4.5A7 7 0 1 1 6.5 13.5Z"/></svg></div>
                    <div className="wa-hinfo"><strong>InsideOut Events</strong><span>Official · Verified</span></div>
                  </div>
                  <div className="wa-msgs">
                    <div className="wa-msg-in">Hi! I just registered for TechSummit 2026. When will I get my ID card?</div>
                    <div className="wa-msg-out">
                      <div className="wa-id-card">
                        <div className="wa-id-thumb"><svg viewBox="0 0 18 18"><rect x="1" y="3" width="16" height="12" rx="2"/><circle cx="6" cy="8" r="2"/><path d="M1 14a5 5 0 0 1 10 0"/></svg></div>
                        <div className="wa-id-info"><strong>Your ID Card is ready</strong><span>TechSummit 2026 · Delegate</span></div>
                      </div>
                      Your ID card for TechSummit Bangalore 2026 is ready! Tap above to view & download. Show the QR at the gate for instant entry. See you tomorrow! 🎉
                      <div className="wa-tick">✓✓ 9:42 AM</div>
                    </div>
                    <div className="wa-msg-in">Wow, that was fast! Thank you 🙏</div>
                  </div>
                  <div className="wa-input">
                    <span>Type a message…</span>
                    <div className="wa-send"><svg viewBox="0 0 14 14"><path d="M1 7l12-6-5 13-2-5z"/></svg></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 4: Dashboard */}
          <div className="feat-row flip">
            <div className="feat-copy reveal">
              <div className="feat-num">04</div>
              <h3>A live command center for your entire event</h3>
              <p>Watch registrations come in, track revenue, monitor check-ins, and export full attendee data — all in one dashboard that updates in real time.</p>
              <div className="feat-tags">
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>Real-time updates</span>
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>CSV export</span>
                <span className="feat-tag"><svg viewBox="0 0 12 12"><path d="M2 2h8v8H2z"/></svg>Revenue tracking</span>
              </div>
            </div>
            <div className="feat-visual reveal d1">
              <div className="ui-panel">
                <div className="dash-panel">
                  <div className="dash-top-row">
                    <span className="dash-title">Event Overview</span>
                    <span className="dash-range">Last 7 days</span>
                  </div>
                  <div className="dash-metrics">
                    <div className="dash-metric"><div className="dash-metric-label">Registered</div><div className="dash-metric-val">1,847</div><div className="dash-metric-delta">↑ 12% this week</div></div>
                    <div className="dash-metric"><div className="dash-metric-label">Revenue</div><div className="dash-metric-val">₹4.2L</div><div className="dash-metric-delta">↑ 8% this week</div></div>
                    <div className="dash-metric"><div className="dash-metric-label">Checked In</div><div className="dash-metric-val">0</div><div className="dash-metric-delta" style={{color:'var(--ink3)'}}>Event tomorrow</div></div>
                  </div>
                  <div className="dash-chart-label">Daily registrations</div>
                  <div className="dash-bars">
                    {[{h:'35%',bg:'#E5E3DC'},{h:'52%',bg:'#C8C5BB'},{h:'48%',bg:'#C8C5BB'},{h:'70%',bg:'#9A9690'},{h:'65%',bg:'#9A9690'},{h:'88%',bg:'#5B4CE4'},{h:'100%',bg:'#5B4CE4'}].map((b,i) => (
                      <div className="dash-bar" style={{height:b.h,background:b.bg}} key={i}></div>
                    ))}
                  </div>
                  <div className="dash-x">{['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => <span key={d}>{d}</span>)}</div>
                  <div className="dash-progress">
                    {[{label:'Speakers',w:'100%',bg:'#5B4CE4',val:'48/48'},{label:'Delegates',w:'82%',bg:'#7B6EF5',val:'1,640'},{label:'VIP',w:'76%',bg:'#1A7A4A',val:'76/100'},{label:'Press',w:'55%',bg:'#B45309',val:'83/150'}].map(p => (
                      <div className="dash-prog-row" key={p.label}>
                        <span className="dash-prog-label">{p.label}</span>
                        <div className="dash-prog-track"><div className="dash-prog-fill" style={{width:p.w,background:p.bg}}></div></div>
                        <span className="dash-prog-val">{p.val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      <section id="stats" className="sec-sm">
        <div className="stats-grain"></div>
        <div className="wrap">
          <div className="stats-inner">
            {[
              {n:'50',sup:'+',label:'Events managed\nacross India'},
              {n:'10K',sup:'+',label:'ID cards\ngenerated'},
              {n:'99.9',sup:'%',label:'Platform\nuptime'},
              {n:'24',sup:'/7',label:'Dedicated\nsupport'},
            ].map((s,i) => (
              <div className={`stat-cell reveal${i>0?' d'+i:''}`} key={s.n}>
                <div className="stat-n">{s.n}<sup>{s.sup}</sup></div>
                <div className="stat-label">{s.label.split('\n').map((l,j) => <React.Fragment key={j}>{l}{j===0&&<br/>}</React.Fragment>)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SMALL FEATURES ─── */}
      <section id="small-feats" className="sec">
        <div className="wrap">
          <div className="sf-label reveal">Also included</div>
          <div className="sf-grid reveal d1">
            {[
              {icon:<path d="M14 2H2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1ZM6 11H4V9h2Zm0-3H4V6h2Zm5 3H7V9h4Zm0-3H7V6h4Z"/>,title:'Bulk CSV upload',desc:'Import hundreds of registrations from Excel or CSV in one click.'},
              {icon:<path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm.75 10.25h-1.5v-5h1.5Zm0-6.5h-1.5v-1.5h1.5Z"/>,title:'Walk-in registration',desc:'Register and print ID cards for walk-ins at the venue in under 30 seconds.'},
              {icon:<path d="M13 3H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1ZM5 9H3V7h2Zm8 0H6V7h7Z"/>,title:'UPI & card payments',desc:'All Razorpay payment modes — UPI, cards, net banking, wallets.'},
              {icon:<path d="M12 1H4a1 1 0 0 0-1 1v12l5-3 5 3V2a1 1 0 0 0-1-1Z"/>,title:'Free & paid tickets',desc:'Mix free passes, paid tickets, and VIP tiers in a single event.'},
              {icon:<path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1ZM3.5 6h9v1.5h-9Zm0 3h9V10.5h-9Z"/>,title:'Data export',desc:'Export full attendee lists, check-in logs, and payment reports anytime.'},
              {icon:<path d="M9 1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5L9 1Zm0 1.5L12.5 5H9V2.5Z"/>,title:'Online booking portal',desc:'A shareable registration page with your event branding, live in minutes.'},
              {icon:<path d="M14 5H2v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V5ZM2 4V2a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v2H2ZM6 1h4v2H6Z"/>,title:'Badge categories',desc:'Different colored cards for Speakers, Delegates, VIPs, Staff, and Press.'},
              {icon:<path d="M13 2H3a1 1 0 0 0-1 1v10l3-2h8a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1Z"/>,title:'24/7 WhatsApp support',desc:'Real humans, not bots. We\'re on WhatsApp the entire week of your event.'},
            ].map(f => (
              <div className="sf-cell" key={f.title}>
                <div className="sf-cell-icon"><svg viewBox="0 0 16 16" fill="currentColor">{f.icon}</svg></div>
                <div className="sf-cell-title">{f.title}</div>
                <div className="sf-cell-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PROCESS ─── */}
      <section id="process" className="sec">
        <div className="wrap">
          <div className="process-grid">
            <div className="reveal">
              <div className="feat-kicker">How it works</div>
              <h2>Live in under an hour.</h2>
              <p>No technical knowledge needed. Book a demo, get onboarded, go live — well before your event day.</p>
            </div>
            <div className="steps-list reveal d1">
              {[
                {n:'01',title:'Set up your event',desc:'Add event details, upload your logo, customize the ID card design, and configure badge categories and registration fields.',pill:'~20 minutes'},
                {n:'02',title:'Open registrations',desc:'Share your registration link. Attendees sign up, pay via Razorpay, and receive their ID card on WhatsApp automatically.',pill:'Shareable link'},
                {n:'03',title:'Run check-in on the day',desc:'Scan QR codes at the gate with any smartphone. Handle walk-ins on-site. Watch every entry appear on your live dashboard.',pill:'Any smartphone'},
              ].map(s => (
                <div className="step-row" key={s.n}>
                  <div className="step-num">{s.n}</div>
                  <div className="step-body">
                    <div className="step-title">{s.title}</div>
                    <p className="step-desc">{s.desc}</p>
                    <span className="step-pill"><svg viewBox="0 0 11 11"><circle cx="5.5" cy="5.5" r="4.5"/></svg>{s.pill}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section id="pricing" className="sec">
        <div className="pricing-grain"></div>
        <div className="wrap pricing-inner">
          <div className="pricing-header reveal">
            <div className="feat-kicker" style={{color:'rgba(255,255,255,.4)',background:'rgba(255,255,255,.07)',display:'inline-block',marginBottom:'16px',padding:'4px 12px',borderRadius:'4px'}}>Pricing</div>
            <h2>One flat rate.<br/><em style={{color:'rgba(255,255,255,.45)',fontStyle:'italic'}}>Everything included.</em></h2>
            <p>No per-attendee fees. No hidden charges. No surprises.</p>
          </div>
          <div className="pricing-layout reveal d1">
            <div className="pricing-side">
              <div className="ps-heading">You get</div>
              {[
                {title:'No setup fee',sub:'Ever. Not now, not later.'},
                {title:'Unlimited participants',sub:'200 or 5,000 — same price.'},
                {title:'Onboarding call included',sub:'We set it up with you, together.'},
                {title:'Cancel before the event',sub:'No questions, no penalties.'},
                {title:'24/7 WhatsApp support',sub:'Real humans the whole week.'},
              ].map(p => (
                <div className="ps-item" key={p.title}>
                  <div className="ps-dot"></div>
                  <div className="ps-text"><strong>{p.title}</strong><span>{p.sub}</span></div>
                </div>
              ))}
            </div>

            <div className="lanyard-stage">
              <div className="lanyard-string"></div>
              <div className="id-price-card" style={{position:'relative'}}>
                <div className="card-eyelet"></div>
                <div className="ipc-header">
                  <div>
                    <div className="ipc-event-name">InsideOut Events</div>
                    <div style={{fontSize:'9px',color:'rgba(255,255,255,.5)',marginTop:'2px',letterSpacing:'.04em'}}>STANDARD PLAN</div>
                  </div>
                  <div className="ipc-logo"><svg viewBox="0 0 14 14"><rect x="1" y="3" width="12" height="9" rx="1.5"/><circle cx="5" cy="7" r="2"/><path d="M1 12a4 4 0 0 1 8 0"/></svg></div>
                </div>
                <div className="ipc-body">
                  <div className="ipc-plan-row">
                    <div className="ipc-plan-label">Per Event</div>
                    <div className="ipc-badge">All features</div>
                  </div>
                  <div className="ipc-price">
                    <span className="ipc-curr">₹</span>
                    <span className="ipc-num">40,000</span>
                    <span className="ipc-per">/ event</span>
                  </div>
                  <div className="ipc-sub">Unlimited participants · No hidden fees</div>
                  <div className="ipc-div"></div>
                  <div className="ipc-features">
                    {['Custom ID cards','QR check-in','Razorpay payments','WhatsApp delivery','Walk-in registration','Live dashboard','Bulk CSV upload','Data export','Booking portal','Badge categories'].map(f => (
                      <div className="ipc-feat" key={f}>
                        <svg viewBox="0 0 13 13"><path d="M2 6.5l3 3 6-6" stroke="#1A7A4A" strokeWidth="1.5" fill="none"/></svg>{f}
                      </div>
                    ))}
                  </div>
                  <button className="ipc-cta" onClick={() => setContactModalOpen(true)}>Book a free demo →</button>
                  <div className="ipc-note">No commitment required. <a href="mailto:technology@insideoutconsult.com">Questions? Talk to us.</a></div>
                </div>
                <div className="ipc-footer">
                  <div className="ipc-footer-left">
                    <strong>Running multiple events?</strong>
                    <span>Custom plans for 5+ events/year</span>
                  </div>
                  <a href="mailto:technology@insideoutconsult.com" className="ipc-footer-link">Contact us →</a>
                </div>
              </div>
              <div className="lanyard-tag">
                <div className="ltag-pills">
                  <span className="ltag-pill">No setup fee</span>
                  <span className="ltag-pill">Cancel anytime</span>
                  <span className="ltag-pill">Onboarding included</span>
                </div>
              </div>
            </div>

            <div className="pricing-side">
              <div className="ps-heading">You don't pay for</div>
              {[
                {title:'Per-attendee fees',sub:'Flat rate regardless of size.'},
                {title:'Feature unlocks',sub:'Every feature is included by default.'},
                {title:'Extra QR scans',sub:'Unlimited check-ins, no metering.'},
                {title:'WhatsApp messages',sub:'Send to all attendees at no extra cost.'},
                {title:'Support tiers',sub:'24/7 support is standard, not premium.'},
              ].map(p => (
                <div className="ps-item" key={p.title}>
                  <div className="ps-dot" style={{background:'rgba(255,255,255,.2)'}}></div>
                  <div className="ps-text"><strong>{p.title}</strong><span>{p.sub}</span></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="sec">
        <div className="wrap">
          <div className="faq-wrap">
            <div className="reveal">
              <h2>Common questions.</h2>
              <p>Anything not covered here — reach us directly. We usually respond within 10 minutes on WhatsApp.</p>
              <a href="https://wa.me/919113854551" className="wa-contact">
                <svg viewBox="0 0 20 20"><path d="M10 1a9 9 0 0 0-9 9c0 1.6.42 3.09 1.15 4.38L1 19l4.75-1.24A9 9 0 1 0 10 1Z"/></svg>
                <div><strong>Chat on WhatsApp</strong><span>Usually replies within 10 min</span></div>
              </a>
            </div>
            <div className="faq-list reveal d1">
              {faqItems.map((item, i) => (
                <div className={`fq-item${openFaq === i ? ' open' : ''}`} key={i}>
                  <div className="fq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                    <span>{item.q}</span>
                    <div className="fq-icon"><svg viewBox="0 0 11 11"><path d="M5.5 1v9M1 5.5h9"/></svg></div>
                  </div>
                  <div className="fq-a">{item.a}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section id="cta" className="sec">
        <div className="cta-grain"></div>
        <div className="wrap">
          <div className="cta-inner">
            <div className="cta-left reveal">
              <h2>Ready to run your next event <em>without the chaos?</em></h2>
              <p>Book a free, no-commitment demo. We'll walk you through the platform and answer every question before you decide.</p>
            </div>
            <div className="cta-right reveal d1">
              <button className="btn-cta-p" onClick={() => setContactModalOpen(true)}>Book a free demo →</button>
              <a href="https://wa.me/919113854551" className="btn-cta-wa">
                <svg viewBox="0 0 18 18"><path d="M9 1a8 8 0 0 0-8 8c0 1.42.38 2.75 1.04 3.9L1 17l4.2-1.1A8 8 0 1 0 9 1Z"/></svg>
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer>
        <div className="wrap">
          <div className="foot-in">
            <span className="foot-brand">InsideOut Events</span>
            <div className="foot-links">
              <a href="#features">Product</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">FAQ</a>
              <a href="#">Privacy</a>
              <a href="#">Terms</a>
              <a href="mailto:technology@insideoutconsult.com">Contact</a>
            </div>
            <span className="foot-copy">© 2025 InsideOut Projects · Made in India · <a href="tel:+919113854551" style={{color:'inherit'}}>+91 91138 54551</a></span>
          </div>
        </div>
      </footer>

      {/* ─── WA FAB ─── */}
      <a href="https://wa.me/919113854551" className="wa-fab" title="Chat on WhatsApp">
        <svg viewBox="0 0 24 24"><path d="M12 2a10 10 0 0 0-10 10c0 1.78.47 3.44 1.3 4.88L2 22l5.24-1.37A10 10 0 1 0 12 2Z"/></svg>
      </a>

      {/* ─── CONTACT MODAL ─── */}
      {contactModalOpen && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setContactModalOpen(false); }}>
          <div className="modal-box">
            <div className="modal-head">
              <button className="modal-close" onClick={() => setContactModalOpen(false)}>×</button>
              <h2>Book a free demo</h2>
              <p>Let's discuss how we can make your event a success.</p>
            </div>
            <form className="modal-body" onSubmit={handleSubmit}>
              <div className="field-row">
                <div className="field-group">
                  <label>Full Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInput} required placeholder="Your name" />
                </div>
                <div className="field-group">
                  <label>Phone Number *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInput} required placeholder="+91 XXXXX XXXXX" />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group">
                  <label>Email Address *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInput} required placeholder="you@company.com" />
                </div>
                <div className="field-group">
                  <label>Company / Organization</label>
                  <input type="text" name="company" value={formData.company} onChange={handleInput} placeholder="Your company" />
                </div>
              </div>
              <div className="field-group">
                <label>Event Type</label>
                <input type="text" name="eventType" value={formData.eventType} onChange={handleInput} placeholder="e.g. Conference, Festival, Corporate" />
              </div>
              <div className="field-group">
                <label>Message</label>
                <textarea name="message" value={formData.message} onChange={handleInput} rows="3" placeholder="Tell us about your event and requirements…"></textarea>
              </div>
              {submitStatus === "success" && <div className="alert-success">✓ Thank you! We'll get back to you shortly.</div>}
              {submitStatus === "error" && <div className="alert-error">Something went wrong. Please try again.</div>}
              <button type="submit" className="btn-submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending…" : "Send message →"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
